import { ICoreContext } from '@msdyn365-commerce/core';
import { computed, toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import getDirectLineTokenAction, { GetDirectLineTokenInput } from '../../actions/get-direct-line-token.action';
import { PrivilegedWrapper } from '../../components/access-control';
import { IUserRoleState } from '../../states/user-role-state';
import { ErrorBoundary } from '../../utilities/global-error-boundary';
import { isJwtExpired } from '../../utilities/token-utils';
import {
    DIRECT_LINE_CONNECTION_OK,
    DIRECT_LINE_CONNECTION_REGION_MISMATCH,
    DIRECT_LINE_CONNECTION_REGION_NOT_ALLOWED,
    DIRECT_LINE_CONNECTION_RESOURCE_NOT_FOUND,
    DIRECT_LINE_CONNECTION_TOKEN_ERROR,
    DIRECT_LINE_CONNECTION_UNKNOWN,
    DIRECT_LINE_EUROPE_DOMAIN,
    ILxsServiceAgentData,
    IStoredConversation,
    RegionalProbeResult,
    STORAGE_KEY
} from './lxs-service-agent.data';
import { ILxsServiceAgentProps } from './lxs-service-agent.props.autogenerated';

/**
 *
 * LxsServiceAgent component
 * @extends {React.PureComponent<ILxsServiceAgentProps<ILxsServiceAgentData>>}
 */

@observer
class LxsServiceAgent extends React.PureComponent<ILxsServiceAgentProps<ILxsServiceAgentData>> {
    private __getConversationStorage = (): Storage | null => {
        try {
            if (typeof window === 'undefined') {
                return null;
            }
            return window.localStorage;
        } catch {
            return null;
        }
    };

    private __getDirectLineToken = async (context: ICoreContext): Promise<string | null> => {
        try {
            if (!context.actionContext) {
                return null;
            }
            const input = new GetDirectLineTokenInput(context.actionContext);
            const directLineTokenResponse = await getDirectLineTokenAction(input, context.actionContext);
            if (!directLineTokenResponse) {
                return null;
            } else {
                return String(directLineTokenResponse.value);
            }
        } catch (err) {
            return null;
        }
    };

    private __readStoredConversation = (): IStoredConversation | null => {
        const storage = this.__getConversationStorage();
        if (!storage) {
            return null;
        }

        try {
            const storedConversationRaw = storage.getItem(STORAGE_KEY);
            if (!storedConversationRaw) {
                return null;
            }
            return JSON.parse(storedConversationRaw) as IStoredConversation;
        } catch {
            storage.removeItem(STORAGE_KEY);
            return null;
        }
    };

    private __writeStoredConversation = (conversation: IStoredConversation): void => {
        const storage = this.__getConversationStorage();
        if (!storage) {
            return;
        }

        try {
            storage.setItem(STORAGE_KEY, JSON.stringify(conversation));
        } catch (e) {
            console.error('Failed to write webchat blob', e);
        }
    };

    private __clearStoredConversation = (): void => {
        const storage = this.__getConversationStorage();
        if (!storage) {
            return;
        }

        try {
            storage.removeItem(STORAGE_KEY);
        } catch {
            return;
        }
    };

    private __getValidStoredConversation = (directLineToken: string, userToken: string): IStoredConversation | null => {
        const tokenExpired = isJwtExpired(String(directLineToken));
        const stored = this.__readStoredConversation();

        if (
            !stored ||
            tokenExpired ||
            stored.directLineToken !== String(directLineToken) ||
            stored.userToken !== userToken ||
            !stored.conversationId ||
            !Array.isArray(stored.transcript)
        ) {
            this.__clearStoredConversation();
            return null;
        }

        return stored;
    };

    private _getProbeErrorCode = async (response: Response): Promise<string | undefined> => {
        try {
            const payload = await response.json();
            return payload?.error?.code;
        } catch {
            return undefined;
        }
    };

    private _probeRegionalDirectLine = async (tokenToValidate: string): Promise<RegionalProbeResult> => {
        const abortController = new AbortController();
        const timeoutId = window.setTimeout(() => abortController.abort(), 8000);

        try {
            const response = await fetch(`${DIRECT_LINE_EUROPE_DOMAIN}/conversations`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${tokenToValidate}`
                },
                signal: abortController.signal
            });

            if (response.status === 403) {
                const errorCode = await this._getProbeErrorCode(response.clone());
                if (errorCode === DIRECT_LINE_CONNECTION_RESOURCE_NOT_FOUND || errorCode === DIRECT_LINE_CONNECTION_REGION_NOT_ALLOWED) {
                    return DIRECT_LINE_CONNECTION_REGION_MISMATCH;
                }
            }

            if (response.status === 401 || response.status === 403) {
                return DIRECT_LINE_CONNECTION_TOKEN_ERROR;
            }

            if (response.ok) {
                return DIRECT_LINE_CONNECTION_OK;
            }

            return DIRECT_LINE_CONNECTION_UNKNOWN;
        } catch {
            return DIRECT_LINE_CONNECTION_UNKNOWN;
        } finally {
            window.clearTimeout(timeoutId);
        }
    };

    private _validateRegionalTokenError = async (tokenToValidate: string): Promise<boolean> => {
        const probeResult = await this._probeRegionalDirectLine(tokenToValidate);
        return probeResult === DIRECT_LINE_CONNECTION_TOKEN_ERROR;
    };

    @computed
    get userRole(): IUserRoleState {
        const raw = toJS(this.props.data?.userRoleState?.result);
        if (!raw) {
            return {} as IUserRoleState;
        }
        return raw;
    }

    public render(): JSX.Element | null {
        const userRole = this.userRole;
        const viewProps = {
            ...this.props,
            CLASS_NAME: 'lxs-service-agent',
            validateRegionalTokenError: this._validateRegionalTokenError,
            probeRegionalDirectLine: this._probeRegionalDirectLine,
            fetchDirectLineToken: this.__getDirectLineToken,
            readStoredConversation: this.__readStoredConversation,
            writeStoredConversation: this.__writeStoredConversation,
            clearStoredConversation: this.__clearStoredConversation,
            getValidStoredConversation: this.__getValidStoredConversation
        };

        return (
            <PrivilegedWrapper allow={['service_agent']} roles={userRole.userRoles}>
                {this.props.renderView(viewProps)}
            </PrivilegedWrapper>
        );
    }
}

export default ErrorBoundary(LxsServiceAgent, 'lxs-service-agent');
